import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../../Common/Configurations/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";
import Header from "../../Common/Layout/Header";
import { format, startOfMonth, endOfMonth } from "date-fns";
import Modal from "react-bootstrap/Modal";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import SubscriptionTabs from '../Subscriptions/SubscriptionsTabs';

// GST Calculation helper functions
const calculateGST = (amount, gstPercentage = 18) => {
  const gstRate = gstPercentage / 100;
  const baseAmount = amount / (1 + gstRate);
  const gstAmount = amount - baseAmount;
  return {
    baseAmount: baseAmount,
    gstAmount: gstAmount,
    totalAmount: amount,
    gstPercentage: gstPercentage
  };
};

const SubscriptionGstReport = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filter States
  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [selectedCompanyName, setSelectedCompanyName] = useState("");

  // Set default dates to current month
  const currentDate = new Date();
  const [fromDate, setFromDate] = useState(format(startOfMonth(currentDate), 'yyyy-MM-dd'));
  const [toDate, setToDate] = useState(format(endOfMonth(currentDate), 'yyyy-MM-dd'));

  // View Details Modal
  const [viewShow, setViewShow] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  // GST Invoice Modal - Individual Payment
  const [gstInvoiceShow, setGstInvoiceShow] = useState(false);
  const [selectedGSTPayment, setSelectedGSTPayment] = useState(null);
  const [gstCalculation, setGstCalculation] = useState(null);
  const [gstPercentage, setGstPercentage] = useState(18);
  const [invoiceNumber, setInvoiceNumber] = useState('');

  // All Companies GST Modal
  const [allGSTInvoiceShow, setAllGSTInvoiceShow] = useState(false);
  const [allGSTData, setAllGSTData] = useState([]);
  const [allGSTSummary, setAllGSTSummary] = useState(null);

  // Refs for PDF generation
  const singleInvoiceRef = useRef(null);
  const consolidatedInvoiceRef = useRef(null);

  // Get all clients for suggestions
  const fetchAllClientsForSuggestions = useCallback(async () => {
    try {
      const resourceUrl = `${config.GETALLCOMPANIES}?companyType=CLIENT`;
      const response = await apiService.getAll(resourceUrl);
      const data = Array.isArray(response) ? response : (response?.data || []);
      setAllClients(data);
    } catch (error) {
      console.error("Failed to preload clients for suggestions:", error);
    }
  }, []);

  // Get all subscription payments with filters
  const GetData = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();

      // Only add companyId if selected
      if (selectedCompanyId) {
        params.append('companyId', selectedCompanyId);
      }

      // Always include date filters
      const from = fromDate || format(startOfMonth(new Date()), 'yyyy-MM-dd');
      const to = toDate || format(endOfMonth(new Date()), 'yyyy-MM-dd');

      params.append('fromDate', from);
      params.append('toDate', to);

      // API call using query parameters
      const url = `${config.GETSUBSCRIPTIONPAYMENTS}?${params.toString()}`;
      const response = await apiService.getAll(url);

      if (response.success && response.data) {
        setPayments(response.data);
      } else {
        setPayments([]);
      }
    } catch (error) {
      console.error("Error fetching payments:", error);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial load - fetch current month data for all companies
  useEffect(() => {
    if (!authLoading && user) {
      fetchAllClientsForSuggestions();
      // Auto load data on initial render
      GetData();
    }
  }, [user, authLoading, fetchAllClientsForSuggestions]);

  // Generate suggestions
  const generateSuggestions = (searchValue) => {
    if (!searchValue || !searchValue.trim()) {
      setSuggestions([]);
      return;
    }

    const searchLower = searchValue.toLowerCase();

    const companyMatches = allClients
      .filter((client) =>
        client.companyName?.toLowerCase().includes(searchLower)
      )
      .slice(0, 5);

    const brandMatches = allClients
      .filter((client) =>
        client.brandName?.toLowerCase().includes(searchLower) &&
        !client.companyName?.toLowerCase().includes(searchLower)
      )
      .slice(0, 3);

    const emailMatches = allClients
      .filter((client) =>
        client.email?.toLowerCase().includes(searchLower) &&
        !client.companyName?.toLowerCase().includes(searchLower) &&
        !client.brandName?.toLowerCase().includes(searchLower)
      )
      .slice(0, 2);

    const allSuggestions = [
      ...companyMatches,
      ...brandMatches,
      ...emailMatches,
    ];
    setSuggestions(allSuggestions);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowSuggestions(true);
    generateSuggestions(value);
  };

  // Handle Search button click - triggers API call
  const handleSearch = () => {
    GetData();
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (client) => {
    setSearchTerm(client.companyName);
    setSelectedCompanyId(client.companyId);
    setSelectedCompanyName(client.companyName);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  // Handle input blur
  const handleInputBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm("");
    setSelectedCompanyId(null);
    setSelectedCompanyName("");
  };

  // Handle Reset
  const handleResetFilters = () => {
    setSearchTerm("");
    setSelectedCompanyId(null);
    setSelectedCompanyName("");
    setFromDate(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
    setToDate(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
    setShowSuggestions(false);
    setSuggestions([]);
    setPayments([]);
  };

  // View payment details
  const handleViewDetails = (payment) => {
    setSelectedPayment(payment);
    setViewShow(true);
  };

  // Handle view modal close
  const handleViewClose = () => {
    setViewShow(false);
    setSelectedPayment(null);
  };

  // Handle Individual GST Invoice from payment
  const handleGSTInvoiceFromPayment = (payment) => {
    setSelectedGSTPayment(payment);
    const gstResult = calculateGST(payment.amount || 0, gstPercentage);
    setGstCalculation(gstResult);
    setInvoiceNumber(payment.invoiceNo || "N/A");
    setGstInvoiceShow(true);
  };

  // Handle GST Invoice modal close
  const handleGSTInvoiceClose = () => {
    setGstInvoiceShow(false);
    setSelectedGSTPayment(null);
    setGstCalculation(null);
    setInvoiceNumber('');
  };

  // Handle GST percentage change
  const handleGSTPercentageChange = (e) => {
    const value = parseFloat(e.target.value) || 0;
    setGstPercentage(value);
    if (selectedGSTPayment) {
      const gstResult = calculateGST(selectedGSTPayment.amount || 0, value);
      setGstCalculation(gstResult);
    }
  };

  // Calculate GST for ALL companies
  const handleAllGSTCalculation = () => {
    const activePayments = payments.filter(
      p => p.status === 'Success' || p.status === 'Active'
    );

    if (activePayments.length === 0) {
      alert("No successful payments found to calculate GST.");
      return;
    }

    const gstData = activePayments.map(payment => {
      const gstResult = calculateGST(payment.amount || 0, gstPercentage);
      return {
        ...payment,
        ...gstResult,
        gstPercentage: gstPercentage
      };
    });

    const summary = {
      totalPayments: gstData.length,
      totalBaseAmount: gstData.reduce((sum, item) => sum + item.baseAmount, 0),
      totalGSTAmount: gstData.reduce((sum, item) => sum + item.gstAmount, 0),
      totalAmount: gstData.reduce((sum, item) => sum + item.totalAmount, 0),
      gstPercentage: gstPercentage
    };

    setAllGSTData(gstData);
    setAllGSTSummary(summary);
    setAllGSTInvoiceShow(true);
  };

  const handleAllGSTClose = () => {
    setAllGSTInvoiceShow(false);
    setAllGSTData([]);
    setAllGSTSummary(null);
  };

  // Download Single Invoice as PDF
  const downloadSingleInvoicePDF = async () => {
    if (!singleInvoiceRef.current) return;

    try {
      const element = singleInvoiceRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`Invoice_${selectedGSTPayment?.companyName || 'Customer'}_${format(new Date(), 'yyyyMMdd')}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  // Download All Companies Consolidated Invoice as PDF
  const downloadConsolidatedInvoicePDF = async () => {
    if (!consolidatedInvoiceRef.current) return;

    try {
      const element = consolidatedInvoiceRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`Companies_Subscription_GST_Report_${format(new Date(), 'yyyyMMdd')}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const printSingleInvoice = () => {
    if (!singleInvoiceRef.current) return;

    const printContents = singleInvoiceRef.current.outerHTML;
    const printWindow = window.open("", "_blank", "width=900,height=700");

    printWindow.document.write(`
      <html>
        <head>
          <title>GST Invoice</title>
          <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css" />
          <style>
            body{ margin:20px; background:#fff; font-family:Arial, sans-serif; }
            table{ width:100%; border-collapse:collapse; }
            table, th, td{ border:1px solid #000; }
            th,td{ padding:8px; }
            @page{ size:A4; margin:10mm; }
            .no-print-gst{ display:none; }
          </style>
        </head>
        <body>
          ${printContents}
        </body>
      </html>
    `);

    printWindow.document.close();

    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const printAllInvoice = () => {
    if (!consolidatedInvoiceRef.current) return;

    const printContents = consolidatedInvoiceRef.current.outerHTML;
    const printWindow = window.open("", "_blank", "width=900,height=700");

    printWindow.document.write(`
      <html>
        <head>
          <title>All Companies GST Report</title>
          <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css" />
          <style>
            body{ margin:20px; background:#fff; font-family:Arial, sans-serif; }
            table{ width:100%; border-collapse:collapse; }
            table, th, td{ border:1px solid #000; }
            th,td{ padding:8px; }
            @page{ size:A4 landscape; margin:10mm; }
            .no-print-gst{ display:none; }
          </style>
        </head>
        <body>
          ${printContents}
        </body>
      </html>
    `);

    printWindow.document.close();

    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return format(new Date(dateString), "dd MMM, yyyy");
  };

  // Get payment status badge color
  const getPaymentStatusBadgeColor = (status) => {
    const colors = {
      'Success': 'bg-success',
      'Pending': 'bg-warning text-dark',
      'Failed': 'bg-danger',
      'Cancelled': 'bg-secondary',
      'Active': 'bg-success',
      'Expired': 'bg-dark'
    };
    return colors[status] || 'bg-secondary';
  };

  // Get plan type badge color
  const getPlanTypeBadgeColor = (planType) => {
    const colors = {
      'Trial': 'bg-info text-dark',
      'Starter': 'bg-primary',
      'Basic': 'bg-success',
      'Premium': 'bg-warning text-dark'
    };
    return colors[planType] || 'bg-secondary';
  };

  // Get billing cycle badge color
  const getBillingCycleBadgeColor = (cycle) => {
    const colors = {
      'Weekly': 'bg-purple',
      'Monthly': 'bg-primary',
      'Quarterly': 'bg-info',
      'Half Yearly': 'bg-warning text-dark',
      'Yearly': 'bg-success'
    };
    return colors[cycle] || 'bg-secondary';
  };

  // Get billing cycle display name
  const getBillingCycleDisplay = (cycle) => {
    if (!cycle) return 'N/A';
    const display = {
      'Weekly': 'Weekly',
      'Monthly': 'Monthly',
      'Quarterly': 'Quarterly',
      'Half Yearly': 'Half Yearly',
      'Yearly': 'Yearly'
    };
    return display[cycle] || cycle;
  };

  // Render payment details
  const renderPaymentDetails = () => {
    if (!selectedPayment) return null;

    return (
      <div className="container-fluid">
        <div className="row mb-3">
          <div className="col-12">
            <h6 className="border-bottom pb-2 mb-3 text-primary">Payment Information</h6>
          </div>
        </div>

        <div className="row mb-2">
          <div className="col-md-6"><strong>Invoice No:</strong></div>
          <div className="col-md-6">
            <code>{selectedPayment.invoiceNo || 'N/A'}</code>
          </div>
        </div>
        <div className="row mb-2">
          <div className="col-md-6"><strong>Company Name:</strong></div>
          <div className="col-md-6">{selectedPayment.companyName || '-'}</div>
        </div>
        <div className="row mb-2">
          <div className="col-md-6"><strong>Company Email:</strong></div>
          <div className="col-md-6">{selectedPayment.companyEmail || '-'}</div>
        </div>
        <div className="row mb-2">
          <div className="col-md-6"><strong>Plan Name:</strong></div>
          <div className="col-md-6">{selectedPayment.planName || '-'}</div>
        </div>
        <div className="row mb-2">
          <div className="col-md-6"><strong>Plan Type:</strong></div>
          <div className="col-md-6">
            <span className={`badge ${getPlanTypeBadgeColor(selectedPayment.planType)}`}>
              {selectedPayment.planType || '-'}
            </span>
          </div>
        </div>
        <div className="row mb-2">
          <div className="col-md-6"><strong>Amount:</strong></div>
          <div className="col-md-6">
            <span className="fw-bold">INR {selectedPayment.amount || 0}</span>
          </div>
        </div>
        
        <div className="row mb-2">
          <div className="col-md-6"><strong>Billing Cycle:</strong></div>
          <div className="col-md-6">
            <span className={`badge ${getBillingCycleBadgeColor(selectedPayment.billingCycle)}`}>
              {getBillingCycleDisplay(selectedPayment.billingCycle)}
            </span>
          </div>
        </div>
        <div className="row mb-2">
          <div className="col-md-6"><strong>Payment Status:</strong></div>
          <div className="col-md-6">
            <span className={`badge ${getPaymentStatusBadgeColor(selectedPayment.status)}`}>
              {selectedPayment.status}
            </span>
          </div>
        </div>

        <div className="row mb-3 mt-3">
          <div className="col-12">
            <h6 className="border-bottom pb-2 mb-3 text-primary">Dates</h6>
          </div>
        </div>

        <div className="row mb-2">
          <div className="col-md-6"><strong>Payment Date:</strong></div>
          <div className="col-md-6">{formatDate(selectedPayment.createdDate)}</div>
        </div>
        <div className="row mb-2">
          <div className="col-md-6"><strong>Subscription Start:</strong></div>
          <div className="col-md-6">{formatDate(selectedPayment.subscriptionStartDate)}</div>
        </div>
        <div className="row mb-2">
          <div className="col-md-6"><strong>Subscription End:</strong></div>
          <div className="col-md-6">{formatDate(selectedPayment.subscriptionEndDate)}</div>
        </div>

        {selectedPayment.razorpayOrderId && (
          <>
            <div className="row mb-3 mt-3">
              <div className="col-12">
                <h6 className="border-bottom pb-2 mb-3 text-primary">Razorpay Details</h6>
              </div>
            </div>
            <div className="row mb-2">
              <div className="col-md-6"><strong>Order ID:</strong></div>
              <div className="col-md-6"><code>{selectedPayment.razorpayOrderId}</code></div>
            </div>
            <div className="row mb-2">
              <div className="col-md-6"><strong>Payment ID:</strong></div>
              <div className="col-md-6"><code>{selectedPayment.razorpayPaymentId}</code></div>
            </div>
          </>
        )}
      </div>
    );
  };

  // Render Individual GST Invoice
  const renderGSTInvoice = () => {
    if (!selectedGSTPayment || !gstCalculation) return null;

    const hasInvoiceNo = selectedGSTPayment.invoiceNo && selectedGSTPayment.invoiceNo !== "";
    // Use payment's created date for invoice date
    const invoiceDate = selectedGSTPayment.createdDate 
      ? new Date(selectedGSTPayment.createdDate) 
      : new Date();

    return (
      <div style={{ padding: '20px', maxWidth: '100%', overflow: 'hidden' }}>
        <div ref={singleInvoiceRef} style={{ background: '#fff', padding: '30px', borderRadius: '8px' }}>
          <style>
            {`
              @media print { .no-print-gst { display: none !important; } }
              .invoice-content { max-width: 100%; overflow: hidden; }
              .table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
              .table-wrap table { min-width: 100%; width: 100%; }
            `}
          </style>

          <div className="invoice-content">
            <div className="row mb-4 align-items-center">
              <div className="col-6">
                <div>
                  <h4 className="mb-1" style={{ fontSize: '24px', fontWeight: 'bold' }}>TAX INVOICE</h4>
                  <p className="text-muted mb-0" style={{ fontSize: '14px' }}>Individual Payment GST Invoice</p>
                </div>
              </div>
              <div className="col-6 text-end">
                <div className="border p-2 rounded bg-light" style={{ minWidth: '200px' }}>
                  <div>
                    <strong>Invoice No:</strong>
                    {hasInvoiceNo ? invoiceNumber : <span className="text-danger">N/A</span>}
                  </div>
                  <div><strong>Date:</strong> {format(invoiceDate, "dd MMM, yyyy")}</div>
                </div>
              </div>
            </div>
            <hr style={{ margin: '10px 0 20px 0' }} />

            <div className="row mb-3">
              <div className="col-6">
                <div className="border p-3 rounded" style={{ background: '#f8f9fa' }}>
                  <h6 className="text-primary" style={{ fontSize: '14px', fontWeight: 'bold' }}>Bill From</h6>
                  <p className="mb-0" style={{ fontSize: '13px' }}><strong>{user?.companyname || "Your Company"}</strong></p>
                  <p className="mb-0" style={{ fontSize: '13px' }}>{user?.companyEmail || "email@company.com"}</p>
                  <p className="mb-0" style={{ fontSize: '13px' }}>{user?.companyPhone || "+91 9876543210"}</p>
                  {user?.companygstno && (
                    <p className="mb-0" style={{ fontSize: '13px' }}><strong>GST No:</strong> {user.companygstno}</p>
                  )}
                </div>
              </div>
              <div className="col-6">
                <div className="border p-3 rounded" style={{ background: '#f8f9fa' }}>
                  <h6 className="text-primary" style={{ fontSize: '14px', fontWeight: 'bold' }}>Bill To</h6>
                  <p className="mb-0" style={{ fontSize: '13px' }}><strong>{selectedGSTPayment.companyName || '—'}</strong></p>
                  <p className="mb-0" style={{ fontSize: '13px' }}>{selectedGSTPayment.companyEmail || '—'}</p>
                  <p className="mb-0" style={{ fontSize: '13px' }}>{selectedGSTPayment.companyPhoneNumber || '—'}</p>
                </div>
              </div>
            </div>

            <div className="table-wrap mt-3">
              <table className="table table-bordered" style={{ fontSize: '13px' }}>
                <thead className="table-light">
                  <tr>
                    <th style={{ padding: '10px' }}>Description</th>
                    <th className="text-end" style={{ padding: '10px' }}>Amount (INR)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: '10px' }}>
                      <strong>Plan: {selectedGSTPayment.planName || 'Subscription'}</strong><br />
                     
                    </td>
                    <td className="text-end" style={{ padding: '10px' }}>
                      <span className="fw-bold">INR {selectedGSTPayment.amount || 0}</span>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan="2" style={{ padding: '10px' }}>
                      <div className="border-top pt-2">
                        <div className="row mb-1">
                          <div className="col-6" style={{ fontSize: '13px' }}>
                            <strong>Base Amount (Excluding GST)</strong>
                          </div>
                          <div className="col-6 text-end" style={{ fontSize: '13px' }}>
                            INR {gstCalculation.baseAmount.toFixed(2)}
                          </div>
                        </div>
                        <div className="row mb-1">
                          <div className="col-6" style={{ fontSize: '13px' }}>
                            <span>GST ({gstPercentage}%)</span>
                          </div>
                          <div className="col-6 text-end" style={{ fontSize: '13px' }}>
                            INR {gstCalculation.gstAmount.toFixed(2)}
                          </div>
                        </div>
                        <div className="row fw-bold fs-5 border-top pt-2">
                          <div className="col-6" style={{ fontSize: '16px' }}>
                            <strong>Grand Total (Including GST)</strong>
                          </div>
                          <div className="col-6 text-end text-success" style={{ fontSize: '16px' }}>
                            INR {gstCalculation.totalAmount.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="text-end text-muted small" style={{ fontSize: '12px' }}>
              GST @ {gstPercentage}%
            </div>

            <div className="border-top pt-3 mt-3">
              <div className="row">
                <div className="col-6">
                  <p className="text-muted small mb-0" style={{ fontSize: '11px' }}>
                    This is a computer-generated GST invoice. No signature required.
                  </p>
                </div>
                <div className="col-6 text-end">
                  <p className="mb-0" style={{ fontSize: '14px', fontWeight: 'bold' }}>{user?.brandName || "Your Company"}</p>
                  <p className="text-muted small" style={{ fontSize: '11px' }}>Authorized Signatory</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="no-print-gst mt-3 d-flex justify-content-end gap-2">
          <button className="btn btn-primary" onClick={printSingleInvoice}>
            <i className="ri-printer-line me-1"></i> Print
          </button>
          <button className="btn btn-success" onClick={downloadSingleInvoicePDF}>
            <i className="ri-file-download-line me-1"></i> Download PDF
          </button>
        </div>
      </div>
    );
  };

  // Render All Companies GST Summary
  const renderAllGSTSummary = () => {
    if (!allGSTSummary || allGSTData.length === 0) return null;

    return (
      <div style={{ padding: '20px', maxWidth: '100%', overflow: 'hidden' }}>
        <div ref={consolidatedInvoiceRef} style={{ background: '#fff', padding: '30px', borderRadius: '8px' }}>
          <style>
            {`
              @media print {
                @page { size: A4 landscape; margin: 8mm; }
                body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                .no-print-gst { display: none !important; }
              }
            `}
          </style>

          <div className="consolidated-invoice-content">
            <div className="row mb-4 align-items-center">
              <div className="col-6">
                <div>
                  <h4 className="mb-1" style={{ fontSize: '24px', fontWeight: 'bold' }}>CONSOLIDATED GST REPORT</h4>
                  <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                    All Companies - Successful Payments
                  </p>
                </div>
              </div>
              <div className="col-6 text-end">
                <div className="border p-2 rounded bg-light" style={{ minWidth: '200px' }}>
                  <div><strong>Report Date:</strong> {format(new Date(), "dd MMM, yyyy")}</div>
                  <div><strong>Period:</strong> {format(new Date(fromDate), "dd MMM")} - {format(new Date(toDate), "dd MMM, yyyy")}</div>
                </div>
              </div>
            </div>
            <hr style={{ margin: '10px 0 20px 0' }} />

            <div className="row mb-3">
              <div className="col-12">
                <div className="border p-3 rounded" style={{ background: '#f8f9fa' }}>
                  <h6 className="text-primary" style={{ fontSize: '14px', fontWeight: 'bold' }}>Bill From</h6>
                  <p className="mb-0" style={{ fontSize: '13px' }}><strong>{user?.companyname || "Your Company"}</strong></p>
                  <p className="mb-0" style={{ fontSize: '13px' }}>{user?.companyEmail || "email@company.com"}</p>
                  <p className="mb-0" style={{ fontSize: '13px' }}>{user?.companyPhone || "+91 9876543210"}</p>
                  {user?.companygstno && (
                    <p className="mb-0" style={{ fontSize: '13px' }}><strong>GST No:</strong> {user.companygstno}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="table-wrap mt-3">
              <table className="table table-bordered table-hover" style={{ fontSize: '13px' }}>
                <thead className="table-light">
                  <tr>
                    <th style={{ padding: '10px' }}>#</th>
                    <th style={{ padding: '10px' }}>Company</th>
                    <th style={{ padding: '10px' }}>Invoice No</th>
                    <th style={{ padding: '10px' }}>Plan</th>
                    <th className="text-end" style={{ padding: '10px' }}>Amount (INR)</th>
                    <th className="text-end" style={{ padding: '10px' }}>Base (INR)</th>
                    <th className="text-end" style={{ padding: '10px' }}>GST (INR)</th>
                    <th className="text-end" style={{ padding: '10px' }}>Total (INR)</th>
                  </tr>
                </thead>
                <tbody>
                  {allGSTData.map((item, index) => {
                    const hasInvoiceNo = item.invoiceNo && item.invoiceNo !== "";
                    return (
                      <tr key={item.subscriptionPaymentId || index}>
                        <td style={{ padding: '10px' }}>{index + 1}</td>
                        <td style={{ padding: '10px' }}><strong>{item.companyName || '-'}</strong></td>
                        <td style={{ padding: '10px' }}>
                          {hasInvoiceNo ? (
                            <code>{item.invoiceNo}</code>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td style={{ padding: '10px' }}>{item.planName || '-'}</td>
                        <td className="text-end" style={{ padding: '10px' }}>INR {item.amount || 0}</td>
                        <td className="text-end" style={{ padding: '10px' }}>INR {item.baseAmount.toFixed(2)}</td>
                        <td className="text-end" style={{ padding: '10px' }}>INR {item.gstAmount.toFixed(2)}</td>
                        <td className="text-end fw-bold" style={{ padding: '10px' }}>INR {item.totalAmount.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="table-dark fw-bold text-dark">
                  <tr>
                    <th colSpan="4" className="text-end" style={{ padding: '10px' }}>TOTAL</th>
                    <th className="text-end" style={{ padding: '10px' }}>INR {allGSTData.reduce((sum, item) => sum + item.amount, 0).toFixed(2)}</th>
                    <th className="text-end" style={{ padding: '10px' }}>INR {allGSTSummary.totalBaseAmount.toFixed(2)}</th>
                    <th className="text-end" style={{ padding: '10px' }}>INR {allGSTSummary.totalGSTAmount.toFixed(2)}</th>
                    <th className="text-end" style={{ padding: '10px' }}>INR {allGSTSummary.totalAmount.toFixed(2)}</th>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="text-end text-muted small" style={{ fontSize: '12px' }}>
              GST @ {gstPercentage}%
            </div>

            <div className="border-top pt-3 mt-3">
              <div className="row">
                <div className="col-6">
                  <p className="text-muted small mb-0" style={{ fontSize: '11px' }}>
                    This is a computer-generated consolidated GST report for all successful payments.
                  </p>
                </div>
                <div className="col-6 text-end">
                  <p className="mb-0" style={{ fontSize: '14px', fontWeight: 'bold' }}>{user?.brandName || "Your Company"}</p>
                  <p className="text-muted small" style={{ fontSize: '11px' }}>Authorized Signatory</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="no-print-gst mt-3 d-flex justify-content-end gap-2">
          <button className="btn btn-primary" onClick={printAllInvoice}>
            <i className="ri-printer-line me-1"></i> Print
          </button>
          <button className="btn btn-success" onClick={downloadConsolidatedInvoicePDF}>
            <i className="ri-file-download-line me-1"></i> Download PDF
          </button>
        </div>
      </div>
    );
  };

  if (authLoading) return <p>Loading...</p>;
  if (!user) {
    navigate("/");
    return null;
  }

  return (
    <>
      <Header />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="card">
              <div className="p-2">
                <SubscriptionTabs pageName="SUBSCRIPTION GST REPORT" />
              </div>

              <div className="card-body">
                {/* Filter Section */}
                <div className="row align-items-end mb-4" style={{ gap: "8px 0" }}>
                  {/* Company Search with Autocomplete - Optional */}
                  <div className="col-md-3">
                    <div style={{ position: "relative" }}>
                      <div className="input-group" style={{ height: "45px" }}>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search company..."
                          value={searchTerm}
                          onChange={handleSearchChange}
                          onFocus={() => setShowSuggestions(true)}
                          onBlur={handleInputBlur}
                          onKeyPress={handleKeyPress}
                          style={{
                            height: "45px",
                            paddingLeft: "40px",
                            position: "relative",
                          }}
                        />
                        <i
                          className="ri-building-2-line"
                          style={{
                            position: "absolute",
                            left: "12px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            zIndex: 10,
                            color: "#6c757d",
                          }}
                        ></i>
                        {searchTerm && (
                          <button
                            className="btn position-absolute end-0 top-0 bottom-0"
                            style={{
                              background: "transparent",
                              border: "none",
                              zIndex: 10,
                            }}
                            onClick={clearSearch}
                          >
                            <i className="ri-close-line fs-5"></i>
                          </button>
                        )}
                      </div>

                      {showSuggestions && suggestions.length > 0 && (
                        <div
                          style={{
                            position: "absolute",
                            width: "100%",
                            marginTop: "4px",
                            border: "1px solid #dee2e6",
                            backgroundColor: "white",
                            borderRadius: "6px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                            zIndex: 1000,
                          }}
                        >
                          {suggestions.map((client) => (
                            <div
                              key={client.companyId}
                              style={{
                                padding: "8px 12px",
                                display: "flex",
                                alignItems: "center",
                                cursor: "pointer",
                                backgroundColor: "white",
                                transition: "background-color 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = "#f8f9fa";
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = "white";
                              }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleSuggestionClick(client);
                              }}
                            >
                              <i className="ri-building-2-line me-2 text-secondary"></i>
                              <div>
                                <div style={{ fontWeight: "600" }}>{client.companyName}</div>
                                {client.brandName && client.brandName !== client.companyName && (
                                  <div style={{ fontSize: "0.875rem", color: "#6c757d" }}>
                                    {client.brandName}
                                  </div>
                                )}
                                {client.email && (
                                  <div style={{ fontSize: "0.875rem", color: "#6c757d" }}>
                                    {client.email}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* From Date */}
                  <div className="col-md-2">
                    <label className="form-label">From Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      style={{ height: "45px" }}
                    />
                  </div>

                  {/* To Date */}
                  <div className="col-md-2">
                    <label className="form-label">To Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      style={{ height: "45px" }}
                    />
                  </div>

                  {/* Search and Reset Buttons */}
                  <div className="col-md-2">
                    <label className="form-label">&nbsp;</label>
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-primary"
                        onClick={handleSearch}
                        style={{ height: "45px", flex: 1 }}
                      >
                        <i className="ri-search-line me-1"></i>
                        Search
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={handleResetFilters}
                        style={{ height: "45px" }}
                      >
                        <i className="ri-refresh-line me-1"></i>
                        Reset
                      </button>
                    </div>
                  </div>
                </div>

                {/* GST Report Buttons */}
                {payments.length > 0 && (
                  <div className="row mb-4">
                    <div className="col-12">
                      <div className="d-flex align-items-end gap-3 flex-wrap">
                        {/* GST Percentage */}
                        <div style={{ width: "120px" }}>
                          <label className="form-label mb-1">GST %</label>
                          <input
                            type="number"
                            className="form-control"
                            value={gstPercentage}
                            onChange={handleGSTPercentageChange}
                            min="0"
                            max="100"
                            step="0.01"
                            style={{ height: "45px" }}
                          />
                        </div>

                        {/* All Companies GST Report */}
                        <div>
                          <button
                            className="btn btn-success"
                            onClick={handleAllGSTCalculation}
                            disabled={
                              payments.filter(
                                p => p.status === "Success" || p.status === "Active"
                              ).length === 0
                            }
                            style={{ height: "45px" }}
                          >
                            <i className="ri-calculator-line me-1"></i>
                            All Companies GST Report
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Table Section */}
                <div className="mt-4">
                  <div className="table-responsive table-card">
                    <table className="table align-middle table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>S.No</th>
                          <th>Invoice No</th>
                          <th>Company</th>
                          <th>Plan</th>
                          <th>Amount</th>
                          <th>Payment Status</th>
                          <th>Payment Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan="8" className="text-center py-4">
                              <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                              <p className="mt-2">Loading payments...</p>
                            </td>
                          </tr>
                        ) : payments.length > 0 ? (
                          payments.map((payment, index) => {
                            const statusClass = getPaymentStatusBadgeColor(payment.status);
                            const planTypeClass = getPlanTypeBadgeColor(payment.planType);
                            const isSuccess = payment.status === 'Success' || payment.status === 'Active';

                            return (
                              <tr key={payment.subscriptionPaymentId || index}>
                                <td>{index + 1}</td>
                                <td>
                                  <code className="bg-light p-1 rounded">
                                    {payment.invoiceNo || 'N/A'}
                                  </code>
                                </td>
                                <td>
                                  <div>
                                    <strong>{payment.companyName || '-'}</strong>
                                  </div>
                                  <small className="text-muted">{payment.companyEmail || ''}</small>
                                </td>
                                <td>
                                  <span className={`badge ${planTypeClass}`}>
                                    {payment.planType || '-'}
                                  </span>
                                  <div><small>{payment.planName || ''}</small></div>
                                </td>
                                <td>
                                  <span className="fw-bold">INR {payment.amount || 0}</span>
                                </td>
                                <td>
                                  <span className={`badge ${statusClass}`}>
                                    {payment.status}
                                  </span>
                                </td>
                                <td>{formatDate(payment.createdDate)}</td>
                                <td>
                                  <ul className="list-inline hstack gap-2 mb-0">
                                    <li
                                      className="list-inline-item"
                                      data-bs-toggle="tooltip"
                                      data-bs-trigger="hover"
                                      data-bs-placement="top"
                                      title="View Details"
                                    >
                                      <Link
                                        className="edit-item-btn"
                                        onClick={() => handleViewDetails(payment)}
                                      >
                                        <i className="ri-eye-fill align-bottom text-primary" />
                                      </Link>
                                    </li>
                                    <li
                                      className="list-inline-item"
                                      data-bs-toggle="tooltip"
                                      data-bs-trigger="hover"
                                      data-bs-placement="top"
                                      title="View Invoice"
                                    >
                                      <button
                                        className="btn btn-sm btn-success"
                                        onClick={() => handleGSTInvoiceFromPayment(payment)}
                                        disabled={!isSuccess}
                                      >
                                        <i className="ri-file-pdf-line me-1"></i>
                                        Invoice
                                      </button>
                                    </li>
                                  </ul>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="8" className="text-center py-4">
                              <i className="ri-file-list-3-line fs-1 text-muted d-block mb-2"></i>
                              No payments found. Click Search to fetch data.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* View Details Modal */}
        <Modal
          show={viewShow}
          onHide={handleViewClose}
          size="lg"
          centered
          scrollable
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="ri-file-list-3-line me-2 text-primary"></i>
              Payment Details
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
            {renderPaymentDetails()}
          </Modal.Body>
          <Modal.Footer>
            <button className="btn btn-danger" onClick={handleViewClose}>
              <i className="ri-close-line me-1"></i> Close
            </button>
          </Modal.Footer>
        </Modal>

        {/* Individual Payment GST Invoice Modal */}
        <Modal
          show={gstInvoiceShow}
          onHide={handleGSTInvoiceClose}
          size="lg"
          centered
          dialogClassName="gst-invoice-modal"
          style={{ maxWidth: '100%' }}
        >
          <Modal.Header closeButton className="no-print-gst">
            <Modal.Title>
              <i className="ri-file-pdf-line me-2 text-success"></i>
              GST Invoice - {selectedGSTPayment?.companyName || ''}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: "80vh", overflowY: "auto", padding: '0' }}>
            {renderGSTInvoice()}
          </Modal.Body>
          <Modal.Footer className="no-print-gst">
            <button className="btn btn-danger" onClick={handleGSTInvoiceClose}>
              <i className="ri-close-line me-1"></i> Close
            </button>
          </Modal.Footer>
        </Modal>

        {/* All Companies GST Summary Modal */}
        <Modal
          show={allGSTInvoiceShow}
          onHide={handleAllGSTClose}
          size="xl"
          centered
          dialogClassName="consolidated-gst-modal"
          style={{ maxWidth: '100%' }}
        >
          <Modal.Header closeButton className="no-print-gst">
            <Modal.Title>
              <i className="ri-calculator-line me-2 text-success"></i>
              All Companies GST Report
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: "80vh", overflowY: "auto", padding: '0' }}>
            {renderAllGSTSummary()}
          </Modal.Body>
          <Modal.Footer className="no-print-gst">
            <button className="btn btn-danger" onClick={handleAllGSTClose}>
              <i className="ri-close-line me-1"></i> Close
            </button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default SubscriptionGstReport;